/**
 * onboarding-forms (US-E23.F7-0337-S01/S03 · @UC9550 @UC9551 @UC9552 · @gate ai) — FORM 3 usecase
 * ONBOARDING: tạo nhóm · thêm thành viên · tạo quỹ.
 *
 * 🔴🔴 ĐÂY LÀ CHỖ NỚI BẤT BIẾN §4 (tập năng lực AI = closed-set lúc boot). PO duyệt 29/07 việc mở rộng
 * tập, nhưng **các chốt còn lại KHÔNG được nới theo** — và file này là nơi giữ chúng:
 *
 *  · AI-INV-1 (AI proposes, core disposes): file này CHỈ mô tả field + validate. **0 I/O, 0 ghi bảng.**
 *    Thực thi nằm ở `apps/api` và đi qua ĐÚNG service mà GUI dùng (`GroupsService.createGroup`,
 *    `MemberImportsService`, `FundsService.createFund`) — KHÔNG đường tắt, KHÔNG endpoint riêng cho AI.
 *  · AI-INV-2 (LLM không bịa số): số tiền là STRING MINOR do NGƯỜI điền vào ô money và engine bóc từ
 *    câu (`vi-normalizer`); LLM chỉ CHỌN biểu mẫu (`inputSchema` rỗng ở write-intent-registry).
 *  · AI-INV-4 (human-in-the-loop): mỗi usecase là MỘT form + MỘT confirm. (US-E24.F14-0363-S04 —
 *    mô hình "wizard" nối 3 bước có thanh tiến trình ĐÃ BỊ GỠ theo QĐ PO 06/08/2026; ba usecase
 *    ONB-* dưới đây chạy ĐỘC LẬP, mỗi cái một form + một confirm.)
 *
 * 🔴 PHẠM VI `ONB-CREATE-GROUP` = ĐÚNG FIELD ENGINE HÔM NAY NHẬN (`name`). `type`/`identityMappingMode`
 * thuộc US-E23.F1-0331 S08 (PR #325 CHƯA merge). Phơi ô nhập mà `createGroup` bỏ qua chính là
 * "toggle no-op" (§3.8) dịch sang ngôn ngữ form: người dùng chọn "nhóm phụ huynh", bấm xác nhận, và
 * nhóm tạo ra không mang loại nào. Thà thiếu ô còn hơn ô nói dối.
 *
 * HÀM THUẦN (DP-3): KHÔNG DB, KHÔNG LLM, KHÔNG Date.now. options (nhóm) do SERVICE nạp THẬT theo user
 * — domain chỉ validate value ∈ options (chống IDOR ở LỚP domain; service re-verify RBAC thật lúc confirm).
 */
import { DomainError } from "../errors.js";
import {
  decodeRows,
  validateFieldValue,
  type SlotFieldSpec,
  type SlotOption,
  type FormState,
} from "./form-slot-filling.js";

/** intentCode ONB-CREATE-GROUP (tạo nhóm). Khớp catalog + router EXACT. */
export const ONB_CREATE_GROUP_INTENT_CODE = "ONB-CREATE-GROUP";

/** intentCode ONB-ADD-MEMBERS (thêm thành viên vào nhóm). Khớp catalog + router EXACT. */
export const ONB_ADD_MEMBERS_INTENT_CODE = "ONB-ADD-MEMBERS";

/** intentCode ONB-CREATE-FUND (tạo quỹ trong nhóm). Khớp catalog + router EXACT. */
export const ONB_CREATE_FUND_INTENT_CODE = "ONB-CREATE-FUND";

/** specVersion từng form (tăng khi đổi chuỗi field — session lưu để phát hiện drift). */
// v2 (US-E4.F1-0388-S04, QĐ PO 12/08/2026): bỏ câu hỏi `groupType` — form còn [tên → category],
// options category nạp từ bảng group_categories (api hydrate), type server suy.
export const ONB_CREATE_GROUP_SPEC_VERSION = 2;
export const ONB_ADD_MEMBERS_SPEC_VERSION = 1;
export const ONB_CREATE_FUND_SPEC_VERSION = 1;

/** Key field tên nhóm. */
export const ONB_GROUP_NAME_KEY = "name";

/** Key field loại nhóm (câu hỏi bước 2 của wizard Figma). */
export const ONB_GROUP_CATEGORY_KEY = "category";

/**
 * 🔴 Shape code category — mirror contracts `GROUP_CATEGORY_CODE_REGEX` (domain KHÔNG dep contracts;
 * đổi một bên đổi cả hai). US-E4.F1-0388 (QĐ PO 12/08/2026): danh sách category KHÔNG còn hardcode
 * ở đây — closed-set sống trong BẢNG `group_categories` (FK enforce), options do api nạp lúc
 * hydrate. Domain chỉ gác HÌNH DẠNG; câu hỏi `groupType` riêng ĐÃ BỎ — mỗi category trong bảng
 * gắn sẵn `type` (luật ánh xạ danh tính), server suy lúc tạo nhóm. Lý do gốc của thiết kế 2 câu
 * cũ ("other/hometown mơ hồ") đã hết vì `other` tách thành individual_person/family_couple.
 */
export const ONB_GROUP_CATEGORY_CODE_REGEX = /^[a-z][a-z0-9_]{1,49}$/;

/** Key field chọn NHÓM (single_choice trên nhóm THẬT user có quyền — service nạp options). */
export const ONB_GROUP_ID_KEY = "groupId";

/** Key field danh sách thành viên (list[name, email, phone]). */
export const ONB_MEMBERS_KEY = "members";

/** Key field tên quỹ. */
export const ONB_FUND_NAME_KEY = "fundName";

/** Key field loại quỹ (quyết định các field tiền phía sau — step-wise). */
export const ONB_FUND_TYPE_KEY = "fundType";

/**
 * Loại quỹ — mirror contracts `FUND_TYPE` (domain KHÔNG dep contracts, nhân bản literal: đổi một bên
 * đổi cả hai). Whitelist khớp CHECK DB `funds.type`.
 */
export const ONB_FUND_TYPES = ["recurring", "goal", "oneoff"] as const;
export type OnbFundType = (typeof ONB_FUND_TYPES)[number];

/** Chu kỳ quỹ định kỳ — mirror contracts `fundCycleSchema`. */
export const ONB_FUND_CYCLES = ["monthly", "quarterly"] as const;

/** Cách chia quỹ một-lần — mirror contracts `fundSplitModeSchema`. */
export const ONB_FUND_SPLIT_MODES = ["equal", "per_head", "custom"] as const;

/** Trần tên nhóm/quỹ — mirror contracts `GROUP_NAME_MAX_LENGTH` / `FUND_NAME_MAX_LENGTH` (đều 50). */
export const ONB_NAME_MAX = 50;

/** Trần số dòng một lần thêm — mirror contracts `IMPORT_ROWS_MAX`. */
export const ONB_MEMBER_ROWS_MAX = 200;

/** Ngày chốt kỳ hợp lệ — mirror contracts `FUND_CLOSE_DAY_MIN/MAX` (1..28, tránh tháng 29-31 ngày). */
export const ONB_CLOSE_DAY_MIN = 1;
export const ONB_CLOSE_DAY_MAX = 28;

/**
 * 🔴🔴 DANH SÁCH CẤM — cùng lý lẽ `VOTE_FORBIDDEN_RULE_FIELD_KEYS`: form onboarding TUYỆT ĐỐI không
 * được hỏi những thứ này, vì hỏi tức là để một câu chat quyết định số dư / bút toán / quyền hạn.
 *
 * Không phải danh sách trang trí: `assertNoForbiddenFields` chạy trên field-seq THẬT của cả ba builder
 * và test khoá. Ai đó thêm `{ key: "balanceMinor" }` vào form ⇒ ném ngay lúc dựng spec.
 */
export const ONB_FORBIDDEN_MONEY_FIELD_KEYS: readonly string[] = Object.freeze([
  "balanceminor",
  "balance",
  "amountminor",
  "openingbalanceminor",
  "ledgertxid",
  "txtype",
  "role",
  "grouprole",
  "ownerid",
]);

/** Payload ONB-CREATE-GROUP đã validate — nối `GroupsService.createGroup`. */
export interface OnbCreateGroupPayload {
  readonly name: string;
  /**
   * Code loại nhóm (bảng `group_categories`). US-E4.F1-0388: server suy `type` (luật ánh xạ
   * danh tính) từ row category lúc tạo — payload KHÔNG mang type nữa; code lạ/paused ⇒
   * 422 GROUP_CATEGORY_INVALID ở service (một điểm enforce duy nhất, form và HTTP cùng đường).
   */
  readonly category: string;
}

/** MỘT dòng thành viên đã chuẩn hoá (email/phone rỗng ⇒ null — KHÔNG chuỗi trắng lọt xuống engine). */
export interface OnbMemberRow {
  readonly name: string;
  readonly email: string | null;
  readonly phone: string | null;
}

/** Payload ONB-ADD-MEMBERS đã validate — nối `MemberImportsService.createBatch` + `confirm`. */
export interface OnbAddMembersPayload {
  readonly groupId: string;
  readonly rows: readonly OnbMemberRow[];
}

/** Sub-config quỹ theo loại — mirror contracts `createFundRequestSchema` (money = STRING minor). */
export type OnbFundSubConfig =
  | { readonly feeMinor: string; readonly cycle: string; readonly closeDay: number }
  | { readonly targetMinor: string }
  | { readonly totalMinor: string; readonly splitMode: string };

/** Payload ONB-CREATE-FUND đã validate — nối `FundsService.createFund`. */
export interface OnbCreateFundPayload {
  readonly groupId: string;
  readonly name: string;
  readonly type: OnbFundType;
  readonly subConfig: OnbFundSubConfig;
}

/**
 * 🔴 CHỐT BẤT BIẾN: field-seq KHÔNG chứa field số dư/bút toán/vai trò. Gọi trong CẢ BA builder ⇒ không
 * thể ship một form onboarding hỏi những thứ đó (fail-loud lúc dựng spec, không đợi runtime).
 */
export function assertNoForbiddenFields(fields: readonly SlotFieldSpec[]): void {
  const forbidden = new Set(ONB_FORBIDDEN_MONEY_FIELD_KEYS);
  for (const f of fields) {
    if (forbidden.has(f.key.toLowerCase())) {
      throw new DomainError(
        "REQUIRED_FIELD_MISSING",
        `Field "${f.key}" không được phép có trong biểu mẫu khởi tạo.`,
      );
    }
    for (const col of f.columns ?? []) {
      if (forbidden.has(col.key.toLowerCase())) {
        throw new DomainError(
          "REQUIRED_FIELD_MISSING",
          `Cột "${col.key}" không được phép có trong biểu mẫu khởi tạo.`,
        );
      }
    }
  }
}

/**
 * Chuỗi field ONB-CREATE-GROUP: `[tên → loại nhóm]` — MỘT câu về loại, đúng bản vẽ Figma.
 *
 * 🔴 VIẾT LẠI (US-E4.F1-0388, QĐ PO 12/08/2026 — đảo docblock cũ "hỏi hai câu"): trước đây phải
 * hỏi riêng `groupType` vì nhãn `other`/`hometown` mơ hồ, không suy được luật ánh xạ. Nay danh
 * mục sống trong BẢNG `group_categories`, mỗi code gắn sẵn `type` (other đã tách thành
 * individual_person/family_couple) ⇒ chọn nhãn LÀ chọn luật, không còn suy diễn mờ.
 * `categoryOptions` do api nạp từ bảng (enabled) lúc hydrate — domain KHÔNG đọc DB, options rỗng
 * ⇒ single_choice reject mọi value (fail-closed như mọi form options-động khác).
 */
export function buildOnbCreateGroupFields(
  categoryOptions: readonly SlotOption[],
): SlotFieldSpec[] {
  const fields: SlotFieldSpec[] = [
    { key: ONB_GROUP_NAME_KEY, type: "text", required: true },
    {
      key: ONB_GROUP_CATEGORY_KEY,
      type: "single_choice",
      required: true,
      options: [...categoryOptions],
    },
  ];
  assertNoForbiddenFields(fields);
  return fields;
}

/**
 * Chuỗi field ONB-ADD-MEMBERS: [chọn nhóm → danh sách thành viên].
 *
 * `email`/`phone` KHÔNG required ở tầng form: người dùng thật hay chỉ có một trong hai (mock Figma:
 * 32 người **chỉ có số điện thoại**). Ép email ⇒ người dùng bịa email để qua ô — tệ hơn nhiều so với
 * việc nhận rồi **NÓI THẲNG** rằng những người đó sẽ không nhận được lời mời (`ONB-ADD-MEMBERS` §S05).
 */
export function buildOnbAddMembersFields(groupOptions: readonly SlotOption[]): SlotFieldSpec[] {
  const fields: SlotFieldSpec[] = [
    {
      key: ONB_GROUP_ID_KEY,
      type: "single_choice",
      required: true,
      options: [...groupOptions],
    },
    {
      key: ONB_MEMBERS_KEY,
      type: "list",
      required: true,
      columns: [
        { key: "name", type: "text", required: true },
        { key: "email", type: "text", required: false },
        { key: "phone", type: "text", required: false },
      ],
    },
  ];
  assertNoForbiddenFields(fields);
  return fields;
}

/**
 * Chuỗi field ONB-CREATE-FUND theo `type` ĐÃ chọn (step-wise — mirror `buildVoteGovernanceFields`):
 *  - chưa chọn ⇒ [groupId, fundName, fundType] (field tiền phụ thuộc loại, chưa dựng được);
 *  - recurring ⇒ + [money(feeMinor), single_choice(cycle), text(closeDay)];
 *  - goal      ⇒ + [money(targetMinor)];
 *  - oneoff    ⇒ + [money(totalMinor), single_choice(splitMode)].
 *
 * Vì sao KHÔNG một field money phẳng: `createFund` không nhận "số tiền" trần — tiền nằm trong sub-config
 * THEO LOẠI (`contracts/funds.ts` superRefine `forbid()` field chéo loại). Một ô money chung sẽ phải
 * đoán nó là fee hay target hay total ⇒ đoán sai = ghi sai bản chất quỹ.
 */
export function buildOnbCreateFundFields(
  groupOptions: readonly SlotOption[],
  type?: string | null,
): SlotFieldSpec[] {
  // 🔴 THỨ TỰ CÓ LÝ DO KỸ THUẬT, ĐỪNG ĐỔI: `fundType` phải đứng TRƯỚC một field required KHÔNG phụ
  // thuộc (`fundName`). Lý do đo được bằng int-spec: `answer()` hydrate spec theo formState TRƯỚC khi
  // ghi slot mới. Nếu `fundType` là field required CUỐI của base thì lượt điền nó làm form "đủ field"
  // ⇒ session nhảy sang `confirm` NGAY, và các ô tiền phụ thuộc loại KHÔNG BAO GIỜ được hỏi ⇒ confirm
  // một quỹ thiếu mức phí/mục tiêu. `fundName` đứng sau giữ form ở trạng thái collecting đúng một lượt
  // để vòng hydrate kế dựng được field tiền. (Mirror `VOTE-GOVERNANCE`: `kind` → `title` → payload.)
  const base: SlotFieldSpec[] = [
    {
      key: ONB_GROUP_ID_KEY,
      type: "single_choice",
      required: true,
      options: [...groupOptions],
    },
    {
      key: ONB_FUND_TYPE_KEY,
      type: "single_choice",
      required: true,
      options: ONB_FUND_TYPES.map((t) => ({ value: t })),
    },
    { key: ONB_FUND_NAME_KEY, type: "text", required: true },
  ];
  let fields: SlotFieldSpec[];
  if (type === "recurring") {
    fields = [
      ...base,
      { key: "feeMinor", type: "money", required: true },
      {
        key: "cycle",
        type: "single_choice",
        required: true,
        options: ONB_FUND_CYCLES.map((c) => ({ value: c })),
      },
      { key: "closeDay", type: "text", required: true },
    ];
  } else if (type === "goal") {
    fields = [...base, { key: "targetMinor", type: "money", required: true }];
  } else if (type === "oneoff") {
    fields = [
      ...base,
      { key: "totalMinor", type: "money", required: true },
      {
        key: "splitMode",
        type: "single_choice",
        required: true,
        options: ONB_FUND_SPLIT_MODES.map((s) => ({ value: s })),
      },
    ];
  } else {
    fields = base;
  }
  assertNoForbiddenFields(fields);
  return fields;
}

/** Ném DomainError cho field bắt buộc còn trống. */
function assertPresent(value: string | null | undefined, field: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new DomainError("REQUIRED_FIELD_MISSING", `Thiếu thông tin bắt buộc: ${field}.`);
  }
  return value.trim();
}

/** Ném DomainError khi tên vượt trần (chốt TRƯỚC biên zod ⇒ thông điệp gọn, 0 row chạm DB). */
function assertNameWithinMax(name: string, label: string): string {
  if (name.length > ONB_NAME_MAX) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `${label} quá dài (tối đa ${ONB_NAME_MAX} ký tự).`,
    );
  }
  return name;
}

/** Chốt `groupId` ∈ options THẬT (chống IDOR ở LỚP domain — service vẫn re-verify RBAC lúc confirm). */
function assertGroupInOptions(
  formState: FormState,
  groupOptions: readonly SlotOption[],
): string {
  const raw = assertPresent(formState[ONB_GROUP_ID_KEY], ONB_GROUP_ID_KEY);
  const validation = validateFieldValue(
    { key: ONB_GROUP_ID_KEY, type: "single_choice", required: true, options: [...groupOptions] },
    raw,
  );
  if (!validation.ok) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      "Nhóm được chọn không nằm trong danh sách nhóm của bạn.",
    );
  }
  return validation.value;
}

/**
 * Validate formState ONB-CREATE-GROUP → payload (fail-fast).
 * US-E4.F1-0388: domain chỉ gác HÌNH DẠNG code (mirror contract) — code có tồn tại/enabled không
 * là việc của bảng `group_categories` ở service (422, một điểm enforce duy nhất cho cả form + HTTP).
 */
export function validateOnbCreateGroupForm(formState: FormState): OnbCreateGroupPayload {
  const name = assertPresent(formState[ONB_GROUP_NAME_KEY], ONB_GROUP_NAME_KEY);
  const category = assertPresent(formState[ONB_GROUP_CATEGORY_KEY], ONB_GROUP_CATEGORY_KEY);
  if (!ONB_GROUP_CATEGORY_CODE_REGEX.test(category)) {
    throw new DomainError("REQUIRED_FIELD_MISSING", "Loại nhóm không hợp lệ.");
  }
  return {
    name: assertNameWithinMax(name, "Tên nhóm"),
    category,
  };
}

/**
 * Validate formState ONB-ADD-MEMBERS → payload (fail-fast, KHÔNG partial).
 *  1. groupId ∈ options THẬT.
 *  2. ≥1 dòng (một lần thêm RỖNG không có nghĩa gì — chặn ở đây thay vì để engine tạo batch trống).
 *  3. ≤ trần 200 dòng (mirror `IMPORT_ROWS_MAX`).
 *  4. mỗi dòng: `name` bắt buộc; email/phone trim, rỗng ⇒ **null** (không để chuỗi trắng lọt xuống
 *     normalize của engine rồi thành "có email" giả).
 */
export function validateOnbAddMembersForm(
  formState: FormState,
  groupOptions: readonly SlotOption[],
): OnbAddMembersPayload {
  const groupId = assertGroupInOptions(formState, groupOptions);
  const rows = decodeRows(formState[ONB_MEMBERS_KEY]);
  if (rows.length === 0) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      "Danh sách thành viên đang trống — bạn thêm ít nhất một người nhé.",
    );
  }
  if (rows.length > ONB_MEMBER_ROWS_MAX) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `Mỗi lần thêm tối đa ${ONB_MEMBER_ROWS_MAX} người.`,
    );
  }
  const normalized = rows.map((row, index) => {
    const name = assertPresent(row.name, `thành viên thứ ${index + 1} (tên)`);
    return {
      name: assertNameWithinMax(name, `Tên thành viên thứ ${index + 1}`),
      email: nullIfBlank(row.email),
      phone: nullIfBlank(row.phone),
    };
  });
  return { groupId, rows: normalized };
}

/** trim → null khi rỗng (một chỗ duy nhất quyết "coi như không có"). */
function nullIfBlank(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * 🔴 @UC9553 (S05) — LÕI THUẦN của cảnh báo "thêm được nhưng KHÔNG mời được".
 *
 * **C1 no-SMS: kênh mời DUY NHẤT là email** (`contracts/member-import.ts` + `IMPORT_SKIP_REASON.
 * PHONE_ONLY_NO_CHANNEL`). Người chỉ có số điện thoại VẪN vào danh sách, nhưng **không ai gửi được lời
 * mời cho họ**. Nếu bot xác nhận im lặng, người dùng tin rằng đã mời 32 người trong khi 32 người đó
 * không nhận được gì.
 *
 * Đếm ở ĐÂY (thuần) để api chỉ việc hiển thị con số — không tự nghĩ ra luật "thế nào là có kênh mời".
 */
export function countMembersWithoutInviteChannel(
  // Index signature CÓ CHỦ Ý: caller truyền nguyên dòng thành viên (name/phone/…), hàm chỉ quan tâm
  // `email`. Không có nó, TypeScript excess-property-check bắt lỗi object literal đầy đủ — và ép
  // caller "cắt bớt" dòng trước khi đếm là mở đường cho việc cắt nhầm.
  rows: readonly { readonly email?: string | null; readonly [key: string]: unknown }[],
): number {
  return rows.filter((r) => nullIfBlank(r.email) === null).length;
}

/**
 * Validate formState ONB-CREATE-FUND → payload (fail-fast, KHÔNG partial).
 *
 * Thứ tự chốt (tất-định): groupId ∈ options → tên ≤ trần → type ∈ whitelist → sub-config THEO LOẠI.
 * Money đi qua `validateFieldValue(type:"money")` — cùng luật no-float mà mọi form tiền khác dùng
 * (KHÔNG viết lại regex tiền lần thứ hai).
 */
export function validateOnbCreateFundForm(
  formState: FormState,
  groupOptions: readonly SlotOption[],
): OnbCreateFundPayload {
  const groupId = assertGroupInOptions(formState, groupOptions);
  const name = assertNameWithinMax(
    assertPresent(formState[ONB_FUND_NAME_KEY], ONB_FUND_NAME_KEY),
    "Tên quỹ",
  );

  const typeRaw = assertPresent(formState[ONB_FUND_TYPE_KEY], ONB_FUND_TYPE_KEY);
  if (!(ONB_FUND_TYPES as readonly string[]).includes(typeRaw)) {
    throw new DomainError("REQUIRED_FIELD_MISSING", "Loại quỹ không hợp lệ.");
  }
  const type = typeRaw as OnbFundType;

  if (type === "recurring") {
    return {
      groupId,
      name,
      type,
      subConfig: {
        feeMinor: assertMoneyMinor(formState, "feeMinor"),
        cycle: assertInWhitelist(formState, "cycle", ONB_FUND_CYCLES, "Chu kỳ không hợp lệ."),
        closeDay: assertCloseDay(formState),
      },
    };
  }
  if (type === "goal") {
    return { groupId, name, type, subConfig: { targetMinor: assertMoneyMinor(formState, "targetMinor") } };
  }
  return {
    groupId,
    name,
    type,
    subConfig: {
      totalMinor: assertMoneyMinor(formState, "totalMinor"),
      splitMode: assertInWhitelist(
        formState,
        "splitMode",
        ONB_FUND_SPLIT_MODES,
        "Cách chia không hợp lệ.",
      ),
    },
  };
}

/** Money minor qua CHÍNH `validateFieldValue` (no-float/no-âm) — không nhân bản luật tiền. */
function assertMoneyMinor(formState: FormState, key: string): string {
  const raw = assertPresent(formState[key], key);
  const validation = validateFieldValue({ key, type: "money", required: true }, raw);
  if (!validation.ok) {
    throw new DomainError("REQUIRED_FIELD_MISSING", validation.message);
  }
  if (validation.value === "0") {
    throw new DomainError("REQUIRED_FIELD_MISSING", "Số tiền phải lớn hơn 0.");
  }
  return validation.value;
}

/** Value ∈ whitelist literal (mirror enum contract — lệch ⇒ 400 TRƯỚC khi chạm engine). */
function assertInWhitelist(
  formState: FormState,
  key: string,
  whitelist: readonly string[],
  message: string,
): string {
  const raw = assertPresent(formState[key], key);
  if (!whitelist.includes(raw)) {
    throw new DomainError("REQUIRED_FIELD_MISSING", message);
  }
  return raw;
}

/** closeDay: nguyên 1..28 (mirror contract). Form lưu string ⇒ parse chặt, KHÔNG `parseInt` lỏng. */
function assertCloseDay(formState: FormState): number {
  const raw = assertPresent(formState.closeDay, "closeDay");
  if (!/^\d{1,2}$/.test(raw)) {
    throw new DomainError("REQUIRED_FIELD_MISSING", "Ngày chốt phải là số nguyên.");
  }
  const day = Number(raw);
  if (day < ONB_CLOSE_DAY_MIN || day > ONB_CLOSE_DAY_MAX) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `Ngày chốt phải trong khoảng ${ONB_CLOSE_DAY_MIN}–${ONB_CLOSE_DAY_MAX}.`,
    );
  }
  return day;
}
